import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { api } from '../../shared/api/client';
import type { ClassroomSummary, MinistriesOverview } from '../../shared/api/types';
import { Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { NewClassroomForm } from './ClassroomsCard';
import { ClassroomCard } from './shared';

/**
 * Raíces: un solo lugar. El alumno ve su salón (si tiene uno solo, entra directo); el maestro, los que
 * enseña; quien dirige Raíces o Raíces Kids, además, el botón para administrarlos.
 */
export function ClassroomsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const [adding, setAdding] = useState(false);
  const data = useAsync(
    async (signal) => {
      if (!churchId) return null;
      const [list, overview] = await Promise.all([
        api.get<{ items: ClassroomSummary[]; candidates?: { personId: string; name: string }[] }>(`/organization/churches/${churchId}/classrooms`, signal),
        api.get<MinistriesOverview>(`/organization/churches/${churchId}/ministries`, signal).catch(() => null),
      ]);
      // Raíces y Raíces Kids que puede administrar (líder, co-líder o administración).
      const manage = (overview?.items ?? []).filter((m) => m.kind === 'raices' && (overview?.canAssignLeaders || m.canAssignCoLeader));
      return { rooms: list.items, candidates: list.candidates ?? [], manage };
    },
    [churchId],
  );
  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  if (!data.data) return null;
  const { rooms, candidates, manage } = data.data;
  // Un alumno con un solo salón entra directo.
  if (manage.length === 0 && rooms.length === 1 && rooms[0]!.role === 'student') return <Navigate to={`/app/salones/${rooms[0]!.id}`} replace />;
  const learn = rooms.filter((r) => r.role === 'student');
  const teach = rooms.filter((r) => r.role === 'teacher');
  const other = rooms.filter((r) => r.role !== 'student' && r.role !== 'teacher');
  const section = (title: string, list: ClassroomSummary[]) =>
    list.length ? (
      <section className="raices-section" aria-label={title}>
        <h2 className="section-title">{title}</h2>
        <ul className="ministry-grid">
          {list.map((c, i) => (
            <ClassroomCard key={c.id} c={c} index={i} />
          ))}
        </ul>
      </section>
    ) : null;
  return (
    <div className="page">
      <header className="page__header">
        <h1>Raíces</h1>
        <p className="muted">Clases de enseñanza de la iglesia.</p>
      </header>
      {teach.length || other.length ? (
        <div className="actions actions--wrap">
          <Link className="btn btn--primary" to="/app/salones/panel">
            Panel del maestro
          </Link>
        </div>
      ) : null}
      {manage.length ? (
        <div className="actions actions--wrap">
          {!adding ? (
            <Button variant="primary" onClick={() => setAdding(true)}>
              + Nuevo salón
            </Button>
          ) : null}
          {manage.map((m) => (
            <Link key={m.id} className="btn btn--secondary" to={`/app/ministerios/${m.id}`}>
              Administrar {m.name}
            </Link>
          ))}
        </div>
      ) : null}
      {adding && churchId ? (
        <NewClassroomForm churchId={churchId} ministries={manage.map((m) => ({ id: m.id, name: m.name, kids: m.systemKey === 'raices_kids' }))} candidates={candidates} onCancel={() => setAdding(false)} />
      ) : null}
      {rooms.length === 0 ? (
        <Card>
          <EmptyState title="Todavía no estás en ningún salón.">
            <p className="muted">Para entrar, pídele a tu maestro o al líder de Raíces que te agregue a un salón.</p>
          </EmptyState>
        </Card>
      ) : null}
      {section(learn.length === 1 ? 'Mi salón' : 'Mis salones', learn)}
      {section('Donde enseño', teach)}
      {section('Todos los salones', other)}
    </div>
  );
}
